import React, { Component, memo } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { colors, distances, grid } from '../../../styles/constants';
import { H2, P } from '../../../components/Typography';
import { AccountIdsProps, withAccountIds } from '../../../auth/accessToken/withAccountIds';
import { Account } from '../../../types/management-auth';
import getConnections from '../../../helpers/getConnections';
import Modal from '../../../components/Modal';
import { Button, ButtonGroup, ButtonLink } from '../../../components/Buttons';
import { Handshake } from '../../../components/nucleoIcons';
import { parseISOStrict } from '../../../helpers/date';
import { isPartner } from '../../../auth/helpers';

export interface WelcomeProps extends WithTranslation, AccountIdsProps {
    getAccount: (accountId: string) => void;
    closeModal: (accountId: string) => void;
    account?: Account;
    showWelcomeModal: boolean;
    exchangeTokenPartnerId: string | undefined;
}

const isNewMerchantAccount = (account?: Account) => {
    // So enterprise users not using payment connections
    // are not nagged after the first week.
    if (!account) {
        return false;
    }
    const createdAt = parseISOStrict(account.created_at || '');
    if (createdAt) {
        const now = new Date();
        const diff = now.valueOf() - createdAt.valueOf();
        const oneWeek = 7 * 24 * 60 * 60 * 1000;
        return diff <= oneWeek;
    }
    return false;
};

const merchantHasNoConnections = (account?: Account) => {
    if (!account) {
        return false;
    }
    const pending = getConnections(account, 'pending');
    const enabled = getConnections(account, 'enabled');
    return pending.length + enabled.length === 0;
};

class Welcome extends Component<WelcomeProps> {
    componentDidMount() {
        const accountId = this.props.prodAccountId || this.props.urlAccountId;
        this.props.getAccount(accountId);
    }

    render() {
        const { t, account, showWelcomeModal, closeModal, exchangeTokenPartnerId } = this.props;
        const accountId = this.props.prodAccountId || this.props.urlAccountId;
        const partner = isPartner(accountId);
        if (
            !partner && // hide modal for partners
            !exchangeTokenPartnerId && // hide modal for Dintero or partner administrators
            isNewMerchantAccount(account) &&
            merchantHasNoConnections(account) &&
            showWelcomeModal
        ) {
            return (
                <Modal>
                    <Wrapper>
                        <H2>{t('welcome.modal.title')}</H2>
                        <Area>
                            <IconWrap>
                                <Handshake fill={colors.text} />
                            </IconWrap>
                            <P>{t('welcome.modal.description_intro')}</P>
                            <br />
                            <P>{t('welcome.modal.description_payments')}</P>
                        </Area>
                        <ButtonGroup>
                            <ButtonLink
                                className="stretch-768"
                                onClick={() => {
                                    closeModal(accountId);
                                }}
                                to={`/${accountId}/settings/payment-connections`}
                            >
                                {t('welcome.modal.go_to_payment_connections')}
                            </ButtonLink>
                            <Button
                                className="alt stretch-768"
                                onClick={() => {
                                    closeModal(accountId);
                                }}
                            >
                                {t('welcome.modal.skip')}
                            </Button>
                        </ButtonGroup>
                    </Wrapper>
                </Modal>
            );
        }
        return null;
    }
}

const Wrapper = styled.div`
    position: relative;
    max-width: ${grid.spans.span8};
`;
const Area = styled.div`
    margin-top: ${distances.normal};
    margin-bottom: ${distances.normal};
`;
const IconWrap = styled.div`
    text-align: center;
    margin-bottom: ${distances.normal};
`;

export default withAccountIds(withTranslation()(memo(Welcome)));
